/* eslint-disable no-console */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import userStorage from 'services/userStorage'
import translatorStorage from 'services/translatorStorage'
import { Navigate, useLocation } from 'react-router-dom'
import { httpService } from 'services/httpService'
import LoadingModal from 'components/Modals/LoadingModal'
import { Roles } from 'utils/enums'
import { loadingContext } from './LoadingContext'

export const AuthContext = createContext({
  logout: async () => {},
  user: {},
})

export const useAuth = () => useContext(AuthContext)
let interval
function AuthProvider({ children }) {
  const { pathname } = useLocation()

  const { setLoading } = useContext(loadingContext)

  const [initialLoading, setInitialLoading] = useState(true)
  const [user, setUser] = useState(userStorage.getUser())

  useEffect(() => {
    const relog = async () => {
      const savedUser = userStorage.getUser()
      if (!savedUser?.accessToken) {
        setInitialLoading(false)
        return clearUserInfo()
      }
      const { headers, code } = await httpService({ endpoint: '/refresh' })
      // eslint-disable-next-line no-console
      console.log('got new accessToken: ', headers?.accesstoken?.slice(-5))
      // can't use useQuery here (hook wouldn't be wrapped by AuthContext)
      if (code === 401) {
        setInitialLoading(false)
        return clearUserInfo()
      }
      if (headers?.accesstoken) {
        login({ ...user, accessToken: headers.accesstoken })
      }
      setInitialLoading(false)
    }
    const ADVANTAGE_TIME = 60000
    interval = setInterval(
      relog,
      +process.env.REACT_APP_ACCESS_TOKEN_EXPIRATION_TIME - ADVANTAGE_TIME,
    )
    relog()
    return () => {
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = (userData) => {
    userStorage.saveUser(userData)
    setUser(userData)
  }

  const logout = async (disableLoading = false) => {
    if (!disableLoading) setLoading(true)
    await httpService({ endpoint: '/refresh?revoke=true' })
    if (!disableLoading) setLoading(false)
    clearUserInfo()
  }

  const clearUserInfo = () => {
    userStorage.removeUser()
    translatorStorage.removeTranslator()
    setUser(null)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const contextObject = useMemo(() => ({ logout, user }), [user])

  if (!user) return <Navigate to='/login' />

  const userMainPath = Roles[user.role].mainPath
  if (!pathname.startsWith(`/${userMainPath}`)) {
    return <Navigate to={`/${userMainPath}`} />
  }

  return (
    <AuthContext.Provider value={contextObject}>
      {initialLoading ? <LoadingModal whiteBackground /> : children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
